import React, { useState, useCallback } from 'react';
import { Text, Button, Stack, Overlay, ControlLink, LoadingBalls } from '@moda/om';
import { formatPrice } from '@moda/portal-stanchions';
import { useCart } from '../../hooks/useCart';
import { useUser } from '../../hooks/useUser';
import { useCheckout } from '../../hooks/useCheckout';
import { useAddressesQuery } from '../../hooks/useAddress';
import { AffirmPromoMessage } from '../AffirmPromoMessage';
import { RewardSummary } from '../Rewards';
import { GiftCode } from '../GiftCode';
import { UserRewardStatus } from '../../constants';
import { hrefFor } from '../../routers';
import { reportError } from '../../lib/reportError';
import { useInternationalDomesticShippingFeeOptimization } from '../../hooks/useInternationalDomesticShippingFeeOptimization';
import { useUsDomesticShippingFeeOptimization } from '../../hooks/useUsDomesticShippingFeeOptimization';
import { PaymentSummaryRow } from './PaymentSummaryRow';

import './CheckoutPaymentSummary.scss';

enum Mode {
  Resting,
  PlacingOrder
}

export const CheckoutPaymentSummary: React.FC = () => {
  const { canPlaceOrder, handlePlaceOrder } = useCheckout();
  const {
    cart: {
      attributes: {
        addressId,
        paymentSummary: {
          vat,
          gst,
          total,
          duties,
          subtotal,
          payToday,
          payLater,
          salesTax,
          gstLabel,
          vatLabel,
          dutyLabel,
          salesLabel,
          siteCredit,
          rewardsAmount,
          totalShipping,
          promotionDiscount,
          giftCardPaymentToday
        },
        rewardPoints,
        rewardSummary
      }
    }
  } = useCart();
  const { addresses } = useAddressesQuery();
  const {
    user: { userLoyaltyStatus }
  } = useUser();
  const [mode, setMode] = useState(Mode.Resting);

  const selectedAddress = addresses.find(address => Number(address.id) === addressId);

  const TAX_DESCRIPTION =
    "Taxes depend on the laws of the location to which you are shipping. Duties are determined by each product's origin, material composition, use, and final destination.";

  const handleSubmit = useCallback(async () => {
    try {
      setMode(Mode.PlacingOrder);
      await handlePlaceOrder().then(() => setMode(Mode.Resting));
      setMode(Mode.Resting);
    } catch (error) {
      reportError('CHECKOUT_PLACE_ORDER', error);
      setMode(Mode.Resting);
    } finally {
      setMode(Mode.Resting);
    }
  }, [handlePlaceOrder, setMode]);

  const orderTotal =
    Number(total.price) - (Number(siteCredit.price) || 0) - (Number(rewardsAmount.price) || 0);

  useUsDomesticShippingFeeOptimization();
  useInternationalDomesticShippingFeeOptimization();

  return (
    <Stack space={4} className="CheckoutPaymentSummary">
      <Text treatment="h4" family="serif">
        Payment Summary
      </Text>
      <div className="CheckoutPaymentSummary__divider" />
      <PaymentSummaryRow
        label="Order Subtotal"
        value={formatPrice(subtotal.currency, subtotal.price)}
      />
      {duties && (
        <PaymentSummaryRow
          label={dutyLabel}
          tooltip={TAX_DESCRIPTION}
          value={formatPrice(duties.currency, duties.price)}
        />
      )}
      {vat && (
        <PaymentSummaryRow
          label={vatLabel}
          tooltip={TAX_DESCRIPTION}
          value={formatPrice(vat.currency, vat.price)}
        />
      )}
      {gst && (
        <PaymentSummaryRow
          label={gstLabel}
          tooltip={TAX_DESCRIPTION}
          value={formatPrice(gst.currency, gst.price)}
        />
      )}
      {salesTax && (
        <PaymentSummaryRow
          label={salesLabel}
          tooltip={TAX_DESCRIPTION}
          value={formatPrice(salesTax.currency, salesTax.price)}
        />
      )}
      <PaymentSummaryRow
        label="Shipping"
        value={
          totalShipping.price == 0
            ? 'Free'
            : formatPrice(totalShipping.currency, totalShipping.price)
        }
      />
      {giftCardPaymentToday?.price !== '0.0' && (
        <PaymentSummaryRow
          label="Gift Card"
          value={`-${formatPrice(giftCardPaymentToday?.currency, giftCardPaymentToday?.price)}`}
        />
      )}
      <RewardSummary theme="simple" format="applied" />
      <GiftCode theme="simple" promotionDiscount={promotionDiscount} />
      {Number(siteCredit.price) > 0 && (
        <PaymentSummaryRow
          label="Site Credit"
          value={`-${formatPrice(siteCredit.currency, siteCredit.price)}`}
        />
      )}
      <Text treatment="bold1">
        <PaymentSummaryRow label="Order Total" value={formatPrice(total.currency, orderTotal)} />
      </Text>
      {rewardSummary?.useLoyaltyRewards && (
        <>
          <div className="CheckoutPaymentSummary__divider" />
          <Text treatment="bold1">Payment Details</Text>
          <PaymentSummaryRow
            label="Pay Today (M'O Rewards)"
            value={`-${formatPrice(rewardsAmount.currency, rewardsAmount.price)}`}
          />
        </>
      )}
      <div className="CheckoutPaymentSummary__divider" />
      <Stack space={1}>
        <Text treatment="bold1">
          <PaymentSummaryRow
            label="Pay Today"
            value={formatPrice(payToday.currency, payToday.price)}
          />
        </Text>
        <div className="CheckoutPaymentSummary__affirm-promo">
          {Number(payLater.price) === 0 &&
            (!selectedAddress || selectedAddress.attributes.countryCode === 'US') && (
              <AffirmPromoMessage pageType="cart" price={Number(payToday.price)} />
            )}
        </div>
      </Stack>
      <PaymentSummaryRow
        label="Remaining Balance"
        tooltip="Preorder items are made especially for you and require a 50% deposit at the time of purchase. The remaining balance and sales tax/duties will be charged upon shipping."
        tooltipAnchor="bottomCenter"
        value={formatPrice(payLater.currency, payLater.price)}
      />
      {userLoyaltyStatus === UserRewardStatus.Enrolled && rewardPoints?.toString() !== '0' && (
        <Text
          data-testid="CheckoutPaymentSummary__earnPoints"
          treatment="bold1"
        >{`Place your order to earn ${rewardPoints} points!`}</Text>
      )}
      <Button disabled={!canPlaceOrder} onClick={handleSubmit} data-testid="checkoutPlaceYourOrder">
        Place your order
      </Button>
      <Stack space={2} direction="horizontal">
        <Text>
          By clicking Place Your Order, you agree to our{' '}
          <ControlLink
            to={hrefFor.TermsAndConditionsPage()}
            className="CheckoutPaymentSummary__terms"
            underlined
            target="_blank"
          >
            Terms &amp; Conditions
          </ControlLink>
          <Text> and </Text>
          <ControlLink
            to={hrefFor.PrivacyPolicyPage()}
            className="CheckoutPaymentSummary__privacy"
            underlined
            target="_blank"
          >
            Privacy Policy.
          </ControlLink>
        </Text>
      </Stack>

      <Overlay className="CheckoutPaymentSummary__overlay" show={mode === Mode.PlacingOrder}>
        <LoadingBalls />

        <Text treatment="eyebrow">Placing order</Text>
      </Overlay>
    </Stack>
  );
};
